import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { useTheme } from '../../themes/theme.hooks';
import { type Theme } from '../../themes/theme.model';

type ToolboxContainerItemProps = Readonly<{
  icon: IconProp;
  title: string;
  className?: string;
  onClick: () => void;
}>;

const itemStyles = (theme: Theme) => css({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  backgroundColor: theme.backgroundColors.primary,
  color: theme.textColors.primary,
  cursor: 'pointer',
  padding: '10px 0 10px 10px',
  textTransform: 'uppercase',

  ':hover': {
    backgroundColor: theme.backgroundColors.secondaryHover,
  },
});

const iconContainerStyles = css({
  display: 'flex',
  justifyContent: 'space-around',
  width: 24, // width of the most wide icon
});

const iconStyles = css({
  fontSize: 20,
  fontWeight: 900,
  lineHeight: 24,
  minWidth: 18,
  borderRadius: 8,
});

const labelStyles = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 500,
});

export const ToolboxItemComponent: React.FC<ToolboxContainerItemProps> = ({
  className,
  onClick,
  icon,
  title,
  ...restProps
}) => {
  const theme = useTheme();

  return (
    <div
      aria-label={title}
      className={className}
      css={itemStyles(theme)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      {...restProps}
    >
      <div css={iconContainerStyles}>
        <FontAwesomeIcon
          css={iconStyles}
          icon={icon}
        />
      </div>

      <div css={labelStyles}>
        {title}
      </div>
    </div>
  );
};
